// Clinical event times retain their charted wall clock and explicit UTC offset.
// Date-only values and legacy unzoned values are deliberately not reinterpreted.
const STAMP = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
export const clinicalTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export function clinicalWallTime(value) {
  const match = typeof value === 'string' && value.trim().replace(' ', 'T').match(STAMP);
  return match ? `${match[1]}:${match[2] || '00'}` : null;
}

export function clinicalOffset(value) {
  const suffix = typeof value === 'string' && value.match(STAMP)?.[3];
  if (!suffix) return null;
  if (suffix === 'Z') return 0;
  const minutes = Number(suffix.slice(1, 3)) * 60 + Number(suffix.slice(4));
  return suffix[0] === '-' ? -minutes : minutes;
}

export function offsetSuffix(minutes) {
  const absolute = Math.abs(minutes);
  return `${minutes < 0 ? '-' : '+'}${String(Math.floor(absolute / 60)).padStart(2, '0')}:${String(absolute % 60).padStart(2, '0')}`;
}

function wallAt(instant, timeZone) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  }).formatToParts(new Date(instant)).map(part => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}

export function captureClinicalNow(timeZone = clinicalTimeZone(), now = new Date()) {
  const wall = wallAt(now, timeZone);
  const offset = Math.round((Date.parse(`${wall}Z`) - Math.floor(now.getTime() / 1000) * 1000) / 60000);
  return `${wall}${offsetSuffix(offset)}`;
}

export function clinicalTimeCandidates(value, timeZone = clinicalTimeZone()) {
  const wall = clinicalWallTime(value);
  if (!wall) return [];
  const base = Date.parse(`${wall}Z`);
  // Reject calendar overflows instead of allowing Date to normalize the input.
  if (!Number.isFinite(base) || new Date(base).toISOString().slice(0, 19) !== wall) return [];
  const offsets = new Set();
  for (let hours = -36; hours <= 36; hours += 6) {
    const sample = base + hours * 3600000;
    offsets.add((Date.parse(`${wallAt(sample, timeZone)}Z`) - sample) / 60000);
  }
  return [...offsets].map(offset => `${wall}${offsetSuffix(offset)}`)
    .filter(candidate => wallAt(Date.parse(candidate), timeZone) === wall)
    .sort((a, b) => Date.parse(a) - Date.parse(b));
}

export function resolveClinicalTime(value, timeZone = clinicalTimeZone(), previousValue) {
  const wall = clinicalWallTime(value);
  if (!wall) return { error: 'Enter a valid date and time.', candidates: [] };
  const candidates = clinicalTimeCandidates(wall, timeZone);
  if (!candidates.length) return { error: 'This time does not exist because the clocks moved forward. Check the time.', candidates };
  const chosen = clinicalOffset(value) !== null ? value
    : clinicalWallTime(previousValue) === wall && clinicalOffset(previousValue) !== null ? previousValue : null;
  if (chosen && candidates.some(candidate => Date.parse(candidate) === Date.parse(chosen))) return { value: chosen, candidates };
  if (candidates.length > 1) return { error: 'This time occurred twice. Choose before or after the clock change.', candidates };
  return { value: candidates[0], candidates };
}

export function clinicalTimeLabel(value, timeZone = clinicalTimeZone()) {
  if (clinicalOffset(value) === null) return '';
  const offset = `UTC${offsetSuffix(clinicalOffset(value))}`;
  try {
    if (wallAt(Date.parse(value), timeZone) !== clinicalWallTime(value)) return offset;
    const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(new Date(value)).find(part => part.type === 'timeZoneName')?.value;
    return name ? `${name} (${offset})` : offset;
  } catch { return offset; }
}

export function clinicalInstant(value) {
  const instant = clinicalOffset(value) === null ? NaN : Date.parse(value);
  return Number.isFinite(instant) ? instant : null;
}

export function clinicalDifferenceSeconds(start, end) {
  if (!clinicalWallTime(start) || !clinicalWallTime(end)) return null;
  const a = clinicalInstant(start), b = clinicalInstant(end);
  if (a !== null && b !== null) return (b - a) / 1000;
  // An unresolved legacy repeated-hour value cannot support an exact interval.
  if ([start, end].some(value => clinicalWallTime(value) && clinicalTimeCandidates(value).length !== 1)) return null;
  const startMs = a ?? new Date(clinicalWallTime(start)).getTime();
  const endMs = b ?? new Date(clinicalWallTime(end)).getTime();
  return Number.isFinite(startMs) && Number.isFinite(endMs) ? (endMs - startMs) / 1000 : null;
}

export const clinicalSortValue = value => clinicalInstant(value) ?? Date.parse(clinicalWallTime(value));

export const preserveClinicalOccurrence = (value, previous) =>
  clinicalWallTime(value) && (clinicalWallTime(value) === clinicalWallTime(previous)
    || value.length === 16 && clinicalWallTime(previous)?.startsWith(value)) ? previous : value;

export function formatClinicalClock(value) {
  const wall = clinicalWallTime(value);
  return wall ? `${wall.replace('T', ' ')}${clinicalTimeLabel(value) ? ` ${clinicalTimeLabel(value)}` : ''}` : value || '';
}

export function normalizeClinicalTimestamp(value) {
  const wall = clinicalWallTime(value);
  if (!wall) return null;
  // Legacy API birth strings sometimes carried Z despite their wall-clock contract.
  // New occurrence-aware values always use a numeric offset supplied by metadata.
  const offset = typeof value === 'string' && value.match(/[+-]\d{2}:\d{2}$/)?.[0];
  return offset ? `${wall}${offset}` : String(value).trim().replace(/Z$/, '').length >= 19 ? wall : wall.slice(0, 16);
}
